import React, { createContext, useState, useEffect, useCallback } from "react";
import { AuthContextType } from "../types/AuthContextType";
import { login as apiLogin, logout as apiLogout, verifyAuth } from "../services/authService";
import { mockIsAuthenticated } from "../services/mockData";

const USE_MOCK_API =
	process.env.REACT_APP_USE_MOCK_API === "true" ||
	!process.env.REACT_APP_API_BASE_URL;

// Note: We no longer try to read cookies directly since they may be HttpOnly
// Instead, we verify authentication by making a request to the backend

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(true);

	// Check authentication status on mount by verifying with backend
	useEffect(() => {
		const checkAuth = async () => {
			if (USE_MOCK_API) {
				const authenticated = mockIsAuthenticated();
				setIsAuthenticated(authenticated);
				setLoading(false);
			} else {
				try {
					// Verify authentication with backend
					const authenticated = await verifyAuth();
					setIsAuthenticated(authenticated);
				} catch (error) {
					console.error("Failed to verify authentication:", error);
					setIsAuthenticated(false);
				} finally {
					setLoading(false);
				}
			}
		};

		checkAuth();
	}, []);

	const login = useCallback(async (username: string, password: string) => {
		try {
			setLoading(true);
			await apiLogin({ username, password });
			// After successful login, verify authentication with backend
			// This ensures the cookie was set correctly and user is authenticated
			const authenticated = await verifyAuth();
			setIsAuthenticated(authenticated);
			if (!authenticated) {
				console.warn("Login succeeded but authentication verification failed");
			}
		} catch (error) {
			setIsAuthenticated(false);
			throw error;
		} finally {
			setLoading(false);
		}
	}, []);

	const logout = useCallback(async () => {
		try {
			setLoading(true);
			await apiLogout();
			setIsAuthenticated(false);
		} catch (error) {
			// Even if logout fails, clear local state
			setIsAuthenticated(false);
			throw error;
		} finally {
			setLoading(false);
		}
	}, []);

	const value: AuthContextType = {
		isAuthenticated,
		login,
		logout,
		loading,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
